import cx from 'classnames'
import { Text } from 'blaze-ui/Text/Text'
import { Credits } from 'blaze-ui/icons/20/Credits'
import { useCreditsToGenerateContent } from 'hooks/useNeedsCreditsToGenerateContent'
import {
  ContentCategory,
  CONTENT_CATEGORIES,
  CONTENT_CATEGORY_CONFIG,
  CONTENT_CATEGORY_THUMBNAILS,
  ContentTypeCounts,
} from 'utils/brandPlans/contentCategories'
import Styles from './ContentTypeDefaults.module.scss'

interface ContentTypeGridProps {
  counts: ContentTypeCounts
  onCountChange: (category: ContentCategory, delta: number) => void
}

const CATEGORY_TO_COST_KEY: Record<
  ContentCategory,
  keyof NonNullable<ReturnType<typeof useCreditsToGenerateContent>['perTypeCosts']>
> = {
  still_image_post: 'stillImage',
  carousel_post: 'carousel',
  feed_video_post: 'feedVideo',
  short_form_video: 'shortFormVideo',
  stories: 'story',
  blog_post: 'blog',
  newsletter: 'email',
}

export const ContentTypeGrid = ({ counts, onCountChange }: ContentTypeGridProps) => {
  const { perTypeCosts } = useCreditsToGenerateContent(0, 0, 0, 0)

  return (
    <div className={Styles.grid}>
      {CONTENT_CATEGORIES.map((category) => {
        const config = CONTENT_CATEGORY_CONFIG[category]
        const thumbnail = CONTENT_CATEGORY_THUMBNAILS[category]
        const count = counts[category]
        const creditsPerPost = perTypeCosts?.[CATEGORY_TO_COST_KEY[category]] ?? 0

        return (
          <div key={category} className={Styles.card}>
            <div className={Styles.thumbnail}>
              <img className={Styles.thumbnailImage} src={thumbnail} alt={config.label} />
            </div>
            <div className={Styles.textBlock}>
              <Text variant="primary">{config.label}</Text>
              <Text variant="metadata" color="var(--dark-60)">
                {config.subtitle}
              </Text>
            </div>
            <div className={Styles.counter}>
              <div className={Styles.counterInput}>
                <input
                  type="text"
                  readOnly
                  className={cx(Styles.counterInputField, { [Styles.zero]: count === 0 })}
                  value={count}
                />
                <button
                  type="button"
                  className={`${Styles.counterButton} ${Styles.decrementButton}`}
                  onClick={() => onCountChange(category, -1)}
                  disabled={count <= 0}
                >
                  −
                </button>
                <button
                  type="button"
                  className={`${Styles.counterButton} ${Styles.incrementButton}`}
                  onClick={() => onCountChange(category, 1)}
                >
                  +
                </button>
              </div>
              <div className={Styles.creditsLabel}>
                <Credits size={14} color="var(--dark-40)" />
                <Text variant="metadata" color="var(--dark-40)">
                  {creditsPerPost} credits per post
                </Text>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
